import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { ROW, PLAYER, FIELD_HALF_TILES, TILE_X, KIDS } from '../core/Constants.js';

const X_LIMIT = FIELD_HALF_TILES * TILE_X;

export const DeathCause = {
  STRUCK: 'struck',
  PIT: 'pit',
  ELECTROCUTED: 'electrocuted',
  CARRIED_OFF: 'carried-off',
  COBRA: 'cobra',
};

/**
 * Resolves the player & trailing children against the lanes they are standing in every frame.
 * Rail lanes kill on contact; yard lanes kill unless a flatcar deck is underfoot.
 */
export class CollisionSystem {
  constructor(player, cobra = null) {
    this.player = player;
    this.cobra = cobra;
  }

  setCobra(cobra) {
    this.cobra = cobra;
  }

  update() {
    const p = gameState.player;
    if (!gameState.game.isPlaying || p.dead) return;

    const isPoisoned = gameState.time < p.curryPoisonUntil;
    if (!isPoisoned) {
      if (gameState.time < p.invulnUntil || gameState.time < p.spicyImmunityUntil) return;
    }

    // Retrieve positions of leader + trailing children based on KIDS.CHECK_COLLISION setting
    const checkKids = (KIDS.CHECK_COLLISION ?? 1) === 1;
    const positions = (checkKids && this.player.getChildrenPositions)
      ? this.player.getChildrenPositions()
      : [{ x: p.x, row: p.row }];

    // 1. Cobra bite collision check for leader & children
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      if (this.cobra && this.cobra.hitsPlayer(pos.x, pos.row, PLAYER.HALF_WIDTH)) {
        if (i === 0) {
          p.carrier = null;
          this.kill(DeathCause.COBRA, null, null, pos.x, pos.row);
          return;
        } else {
          this.killChild(i - 1, DeathCause.COBRA, pos.x, pos.row);
          return;
        }
      }
    }

    // 2. Check trailing children collisions (struck by trains or falling into electrified pits)
    for (let i = 1; i < positions.length; i++) {
      const pos = positions[i];
      if (pos.row < 0) continue;

      const r = gameState.level.rows[pos.row];
      if (!r || r.type === ROW.GRASS) continue;

      if (r.type === ROW.RAIL) {
        const consist = r.lane.hits(pos.x, PLAYER.HALF_WIDTH);
        if (consist) {
          this.killChild(i - 1, DeathCause.STRUCK, pos.x, pos.row, r.lane, consist);
          return;
        }
      } else if (r.type === ROW.FREIGHT) {
        const consist = r.lane.carrierAt(pos.x);
        if (!consist) {
          this.killChild(i - 1, DeathCause.ELECTROCUTED, pos.x, pos.row, r.lane);
          return;
        }
      }
    }

    // 3. Mid-hop the leader is airborne.
    if (this.player.hop) {
      p.carrier = null;
      return;
    }

    // Check main player lane standing state (carrier vs rail struck vs electrocuted)
    const leaderRow = gameState.level.rows[p.row];

    if (!leaderRow || leaderRow.type === ROW.GRASS) {
      p.carrier = null;
    } else if (leaderRow.type === ROW.RAIL) {
      p.carrier = null;
      const consist = leaderRow.lane.hits(p.x, PLAYER.HALF_WIDTH);
      if (consist) {
        this.kill(DeathCause.STRUCK, leaderRow.lane, consist, p.x, p.row);
        return;
      }
    } else if (leaderRow.type === ROW.FREIGHT) {
      const consist = leaderRow.lane.carrierAt(p.x);
      if (!consist) {
        p.carrier = null;
        this.kill(DeathCause.ELECTROCUTED, leaderRow.lane, null, p.x, p.row);
        return;
      }
      if (!p.carrier || p.carrier.consist !== consist) {
        p.carrier = { lane: leaderRow.lane, consist };
        eventBus.emit(Events.PLAYER_BOARD);
      }
      if (Math.abs(p.x) > X_LIMIT + TILE_X * 0.5) {
        this.kill(DeathCause.CARRIED_OFF, leaderRow.lane, p.carrier?.consist, p.x, p.row);
        return;
      }
    }
  }

  killChild(childIndex, cause, x, row, lane = null, consist = null) {
    if (this.player && this.player.removeChild) {
      this.player.removeChild(childIndex, cause, x, row, lane, consist);
    }
  }

  kill(cause, lane = null, consist = null, x = gameState.player.x, row = gameState.player.row) {
    const p = gameState.player;
    if (p.dead) return;
    p.dead = true;
    p.carrier = null;
    eventBus.emit(Events.PLAYER_DIE, { cause, x, row, lane, consist });
  }
}
